import { useEffect, useState } from 'react';
import api from '../services/api';
import { Spinner } from './ui';
import { BTN_SOFT, BTN_SOFT_STYLE } from '../lib/ui';
import { IconExternal } from './NavIcons';
import { openProtectedFile } from '../lib/http';

type Props = {
  fileUrl: string;
  fileName: string;
  mimeType?: string | null;
  className?: string;
};

/** In-page viewer for protected PDFs/images (auth via bearer blob fetch). */
export default function DocumentViewer({ fileUrl, fileName, mimeType, className = '' }: Props) {
  const [src, setSrc] = useState<string | null>(null);
  const [type, setType] = useState(mimeType || '');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSrc(null);

    void api
      .get<Blob>(fileUrl, { responseType: 'blob', timeout: 120000 })
      .then(({ data, headers }) => {
        if (cancelled) return;
        const resolved = String(headers['content-type'] || data.type || mimeType || 'application/octet-stream');
        const blob = new Blob([data], { type: resolved });
        objectUrl = URL.createObjectURL(blob);
        setType(resolved);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load this file.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileUrl, mimeType]);

  const isPdf = type.includes('pdf') || fileName.toLowerCase().endsWith('.pdf');
  const isImage = type.startsWith('image/') || /\.(png|jpe?g|webp|gif)$/i.test(fileName);

  return (
    <div className={`flex min-h-[28rem] flex-col overflow-hidden rounded-2xl border border-[#fce7f3] bg-[#fffafc] ${className}`}>
      <div className="flex items-center justify-between gap-2 border-b border-[#fce7f3] px-3 py-2">
        <p className="min-w-0 truncate text-xs font-semibold text-[#6b0030]">{fileName}</p>
        <button
          type="button"
          onClick={() => void openProtectedFile(fileUrl)}
          className={`${BTN_SOFT} py-1.5`}
          style={BTN_SOFT_STYLE}
          title="Open in new tab"
        >
          <IconExternal className="h-3.5 w-3.5" /> New tab
        </button>
      </div>
      <div className="relative min-h-0 flex-1 bg-[#f8f0f4]">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-[#b0708f]">
            <Spinner /> Loading document…
          </div>
        )}
        {error && !loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
            <p className="text-sm text-[#be123c]">{error}</p>
            <button type="button" onClick={() => void openProtectedFile(fileUrl)} className={BTN_SOFT} style={BTN_SOFT_STYLE}>
              Try opening in a new tab
            </button>
          </div>
        )}
        {src && !error && isPdf && (
          <iframe title={fileName} src={src} className="h-full min-h-[28rem] w-full border-0" />
        )}
        {src && !error && isImage && (
          <div className="flex h-full min-h-[28rem] items-center justify-center overflow-auto p-4">
            <img src={src} alt={fileName} className="max-h-[70vh] max-w-full object-contain shadow-sm" />
          </div>
        )}
        {src && !error && !isPdf && !isImage && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
            <p className="text-sm text-[#6b3a52]">Preview is not available for this file type.</p>
            <button type="button" onClick={() => void openProtectedFile(fileUrl)} className={BTN_SOFT} style={BTN_SOFT_STYLE}>
              <IconExternal className="h-3.5 w-3.5" /> Download / open
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
